"use client";

import { createTheme } from "@mui/material/styles";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import type { ThemeMode } from "./theme-config";

// Paleta de superficies/entradas dependiente del modo. En oscuro espejan los
// tokens CSS de `globals.css` (.dark) para que MUI y Tailwind sean coherentes.
function surfaces(mode: ThemeMode) {
  const isDark = mode === "dark";
  return {
    isDark,
    bgDefault: isDark ? "#0a0a0a" : "#F9FAFB",
    bgPaper: isDark ? "#161618" : "#fff",
    inputBg: isDark ? "#1c1c1f" : "#fff",
    border: isDark ? "#2a2a2e" : "#D1D5DB",
    borderHover: isDark ? "#3d3d42" : "#9CA3AF",
    label: isDark ? "#b8b8bb" : "#374151",
    textPrimary: isDark ? "#ededed" : "#111827",
    textSecondary: isDark ? "#8b8b8e" : "#4B5563",
  };
}

/**
 * Construye el tema MUI para el modo dado. La variante clara conserva el
 * comportamiento histórico; la oscura ajusta paleta, superficies y los
 * `backgroundColor`/bordes/labels de las entradas (antes cableados a blanco).
 */
export function createAppTheme(mode: ThemeMode = "light") {
  const s = surfaces(mode);
  return createTheme({
  // Nuestro Modal custom (`@/components/common/ui/feedback/Modal`) se renderiza
  // con `z-index: 2000`. Los Popovers/Menus de MUI usan por defecto
  // `theme.zIndex.modal = 1300`, así que cualquier `<Select>` o
  // `<Autocomplete>` abierto dentro de uno de nuestros Modals salía
  // POR DETRÁS. Subimos toda la pila de z-index de MUI por encima del
  // overlay custom (2000) para que los popups queden visibles.
  zIndex: {
    mobileStepper: 2050,
    fab: 2050,
    speedDial: 2050,
    appBar: 2100,
    drawer: 2200,
    modal: 2300,
    snackbar: 2400,
    tooltip: 2500,
  },
  palette: {
    mode: s.isDark ? "dark" : "light",
    background: {
      default: s.bgDefault,
      paper: s.bgPaper,
    },
    text: {
      primary: s.textPrimary,
      secondary: s.textSecondary,
    },
    divider: s.border,
    primary: {
      main: "#0070B8", // azul de marca (el del icono)
      light: "#3390CC", // tinte del mismo azul (sin celeste)
      dark: "#005A94",
      contrastText: "#fff",
    },
    secondary: {
      main: "#0070B8", // azul de marca
      light: "#3390CC",
      dark: "#005A94",
    },
    error: {
      main: "#EF4444",
    },
    success: {
      main: "#10B981",
    },
    warning: {
      main: "#F59E0B",
    },
    info: {
      main: "#3B82F6",
    },
    grey: {
      50: "#F9FAFB",
      100: "#F3F4F6",
      200: "#E5E7EB",
      300: "#D1D5DB",
      400: "#9CA3AF",
      500: "#6B7280",
      600: "#4B5563",
      700: "#374151",
      800: "#1F2937",
      900: "#111827",
    },
  },
  typography: {
    fontFamily: "var(--font-geist-sans), system-ui, -apple-system, sans-serif",
    fontSize: 14,
  },
  shape: {
    borderRadius: 8, // rounded-lg
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: s.inputBg,
            fontSize: "0.875rem", // text-sm
            "& fieldset": {
              borderColor: s.border, // gray-300 (claro) - más claro que antes
              borderWidth: "1px",
            },
            "&:hover fieldset": {
              borderColor: s.borderHover, // gray-400 (claro)
            },
            "&.Mui-focused fieldset": {
              borderColor: "#0070B8", // azul de marca
              borderWidth: "2px",
            },
            "&.Mui-error fieldset": {
              borderColor: "#EF4444",
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
            color: s.label, // gray-700 (claro)
            "&.Mui-focused": {
              color: "#0070B8", // azul de marca
            },
            "&.Mui-error": {
              color: "#EF4444",
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "0.875rem",
            padding: "8.5px 14px", // Consistente con size small
          },
          "& .MuiInputBase-inputMultiline": {
            padding: "0px",
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: "outlined",
        size: "medium",
      },
      styleOverrides: {
        root: {
          backgroundColor: s.inputBg,
          fontSize: "0.875rem",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: s.border, // gray-300 (claro)
            borderWidth: "1px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: s.borderHover, // gray-400 (claro)
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0070B8", // azul de marca
            borderWidth: "2px",
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#EF4444",
          },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
            color: s.label, // gray-700 (claro)
            "&.Mui-focused": {
              color: "#0070B8", // azul de marca
            },
            "&.Mui-error": {
              color: "#EF4444",
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          color: "#374151", // gray-700
          "&.Mui-focused": {
            color: "#0070B8", // azul de marca
          },
          "&.Mui-error": {
            color: "#EF4444",
          },
        },
        asterisk: {
          color: "#EF4444", // red-500
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: s.inputBg,
          fontSize: "0.875rem",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: s.border, // gray-300 (claro)
            borderWidth: "1px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: s.borderHover, // gray-400 (claro)
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0070B8", // azul de marca
            borderWidth: "2px",
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#EF4444",
          },
        },
        input: {
          fontSize: "0.875rem",
          padding: "8.5px 14px",
        },
        inputMultiline: {
          padding: "0px",
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: s.inputBg,
          fontSize: "0.875rem",
          "& .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: s.border, // gray-300 (claro)
            borderWidth: "1px",
          },
          "&:hover .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: s.borderHover, // gray-400 (claro)
          },
          "&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "#0070B8", // azul de marca
            borderWidth: "2px",
          },
          "&.Mui-error .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "#EF4444",
          },
        },
      },
    },
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          "& .MuiPickersOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "#0070B8", // azul de marca
            borderWidth: "2px",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          "&:hover": {
            backgroundColor: "rgba(66, 165, 245, 0.08)", // brand-light/8
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(66, 165, 245, 0.12)", // brand-light/12
            "&:hover": {
              backgroundColor: "rgba(66, 165, 245, 0.16)", // brand-light/16
            },
          },
        },
      },
    },
    // ── Cinturón-y-tirantes: forzamos zIndex en los Popper/Popover/Modal
    // de MUI para garantizar que SIEMPRE queden por encima de nuestro
    // Modal custom (z 2000), aunque algún componente o build viejo no
    // resuelva bien `theme.zIndex.modal`.
    MuiPopover: {
      styleOverrides: {
        root: { zIndex: 2300 },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: { zIndex: 2300 },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: { zIndex: 2300 },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: { zIndex: 2300 },
      },
    },
    // Fondo de drawers y diálogos de MUI: el mismo velo oscuro con desenfoque
    // que usa el Modal de la app (`bg-black/50 backdrop-blur-sm`). Los
    // backdrops invisibles (menús, popovers) se dejan como están.
    MuiBackdrop: {
      styleOverrides: {
        root: {
          "&:not(.MuiBackdrop-invisible)": {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: { zIndex: 2300 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: { zIndex: 2500 },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
          marginLeft: 0,
          marginTop: "4px",
          "&.Mui-error": {
            color: "#EF4444",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "0.5rem",
          fontWeight: 500,
          fontSize: "0.875rem",
          // Misma altura que el resto de controles de cabecera (40 px).
          minHeight: 40,
        },
      },
    },
  },
  });
}

// Compatibilidad: tema claro por defecto para imports existentes.
export const theme = createAppTheme("light");
